import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useInstitution } from '../contexts/InstitutionContext';
import { FirebaseError } from 'firebase/app';
import { useNavigate, Link } from 'react-router-dom';
import { preserveInstitutionParams } from '../utils/navigation';
import Sidebar from './Sidebar';
import AppNavigation from './AppNavigation';
import { api } from '../services/api';

export default function Login() {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoginAllowed, setGoogleLoginAllowed] = useState(true); // New state for Google login permission
  const [signupsAllowed, setSignupsAllowed] = useState(true); // New state for signup permission
  const { login, register, loginWithGoogle, getRedirectResult, currentUser } = useAuth();
  const { institution, theme, applyTheme, institutionName, loading: institutionLoading } = useInstitution();
  const navigate = useNavigate();

  // Get the base path from CSS variable
  const basePath = getComputedStyle(document.documentElement).getPropertyValue('--base-path').trim();

  // Apply institution theming
  useEffect(() => {
    applyTheme();
  }, [theme, applyTheme]);

  // Check if Google login is allowed for the current institution
  useEffect(() => {
    const checkGoogleLoginPermission = async () => {
      try {
        const result = await api.institution.checkGoogleLoginAllowed();
        setGoogleLoginAllowed(result.google_login_allowed);
      } catch (error) {
        console.error('Error checking Google login permission:', error);
        // Default to allowing Google login in case of error
        setGoogleLoginAllowed(true);
      }
    };

    // Check institution signup settings
    const checkSignupPermission = () => {
      // Wait for institution loading to complete before making decisions
      if (institutionLoading) {
        return;
      }

      if (institution) {
        // If institution exists, check its allow_signups setting
        setSignupsAllowed(institution.allow_signups ?? true);
      } else {
        // Default platform always allows signups
        setSignupsAllowed(true);
      }
    };

    checkGoogleLoginPermission();
    checkSignupPermission();
  }, [institution, institutionLoading]);

  // Check for redirect result on component mount
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        console.log('Checking for Google redirect result');
        setLoading(true);
        const result = await getRedirectResult();
        if (result?.user) {
          console.log('Successfully signed in with Google redirect', result.user.email);
          navigate(preserveInstitutionParams('/'));
        }
      } catch (err) {
        console.error('Google redirect error:', err);
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    handleRedirectResult();
  }, [getRedirectResult, navigate]);

  // Redirect if user is already logged in
  useEffect(() => {
    if (currentUser) {
      console.log('User already authenticated, redirecting to home');
      navigate(preserveInstitutionParams('/'));
    }
  }, [currentUser, navigate]);

  // Ensure institutions that don't allow signups are always in login mode
  useEffect(() => {
    if (institution && !signupsAllowed) {
      setIsLogin(true);
    }
  }, [institution, signupsAllowed]);

  // Function to get user-friendly error message
  const getErrorMessage = (error: unknown): string => {
    if (error instanceof FirebaseError) {
      // Handle specific Firebase auth error codes
      switch (error.code) {
        case 'auth/email-already-in-use':
          return t('login.errors.emailInUse');
        case 'auth/weak-password':
          return t('login.errors.weakPassword');
        case 'auth/invalid-email':
          return t('login.errors.invalidEmail');
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          return t('login.errors.userNotFound');
        case 'auth/too-many-requests':
          return t('login.errors.tooManyRequests');
        case 'auth/network-request-failed':
          return t('login.errors.networkError');
        case 'auth/popup-closed-by-user':
          return t('login.errors.popupClosed');
        case 'auth/popup-blocked':
          return t('login.errors.popupBlocked');
        case 'auth/cancelled-popup-request':
          return t('login.errors.cancelledPopup');
        default:
          return t('login.errors.authError', { message: error.message || error.code });
      }
    }
    return error instanceof Error ? error.message : t('login.errors.authFailed');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // First, validate email domain for institution restrictions
      console.log('Validating email domain before authentication:', email);
      const domainValidation = await api.institution.validateEmailDomain(email);
      
      if (!domainValidation.valid) {
        setError(domainValidation.error || 'Email domain not allowed for this institution');
        return;
      }
      
      if (isLogin) {
        console.log('Attempting login for:', email);
        await login(email, password);
      } else {
        console.log('Attempting registration for:', email);
        await register(email, password);
        
        // After successful registration, validate email domain and assign institution
        try {
          console.log('Validating email domain for institutional assignment:', email);
          const validationResult = await api.auth.registerValidate(email);
          
          if (validationResult.auto_assigned) {
            console.log('✅ User auto-assigned to institution:', validationResult.institution_name);
          } else {
            console.log('ℹ️ No institution match for email domain');
          }
        } catch (domainValidationError) {
          console.warn('Email domain validation failed (non-critical):', domainValidationError);
          // Don't block registration for domain validation failures
        }
      }
      
      // Navigation will happen via the useEffect watching currentUser
    } catch (err) {
      console.error('Authentication error:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      console.log('Initiating Google sign-in');
      const result = await loginWithGoogle();
      
      if (result && result.user) {
        console.log('Google sign-in successful via popup');
        
        // If user logged in via Google on an institution subdomain, auto-assign them to that institution
        if (institution && result.user.email) {
          try {
            console.log(`Auto-assigning user ${result.user.email} to institution ${institution.name}`);
            await api.auth.registerValidate(result.user.email);
            console.log('Auto-assignment completed successfully');
          } catch (assignError) {
            console.warn('Auto-assignment failed:', assignError);
            // Don't fail the login if auto-assignment fails
          }
        }
        
        // Navigation will happen via the useEffect watching currentUser
      }
      // If result is null, the auth was done via redirect and will be handled on page reload
    } catch (err) {
      console.error('Google authentication error:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    // Prevent mode switching for institutions that don't allow signups
    if (institution && !signupsAllowed) return;
    
    setIsLogin(!isLogin);
    setError(''); // Clear any existing errors when switching modes
  };

  return (
    <div className="app-container">
      <Sidebar currentUser={currentUser} />
      
      <div className="main-content with-sidebar">
        <AppNavigation />
        
        <div className="App">
          <div className="auth-outer-container">
            <div className="auth-logo-outer">
              {institution && institution.logo_url ? (
                <img 
                  src={institution.logo_url}
                  alt={`${institutionName} Logo`} 
                  width="160" 
                  height="auto"
                />
              ) : (
                <img 
                  src={`${basePath}/maxel_logo.svg`}
                  alt="Maxel Logo" 
                  width="160" 
                  height="auto"
                />
              )}
            </div>
            
            <div className="auth-mode-toggle">
              <div className="auth-mode-text">
                {/* Show login/signup based on mode, except for institutions that don't allow signups */}
                {(institution && !signupsAllowed) ? t('login.title.login') : (isLogin ? t('login.title.login') : t('login.title.signup'))}
              </div>
            </div>
            
            <div className="auth-container">
              {error && <div className="error-message">{error}</div>}
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="email">{t('login.form.email')}</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="password">{t('login.form.password')}</label>
                  <div className="password-field">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <div 
                      className="eye-icon" 
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14.53 9.47L9.47 14.53C8.82 13.88 8.42 12.99 8.42 12C8.42 10.02 10.02 8.42 12 8.42C12.99 8.42 13.88 8.82 14.53 9.47Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M17.82 5.77C16.07 4.45 14.07 3.73 12 3.73C8.47 3.73 5.18 5.81 2.89 9.41C1.99 10.82 1.99 13.19 2.89 14.6C3.68 15.84 4.6 16.91 5.6 17.77" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8.42 19.53C9.56 20.01 10.77 20.27 12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.4C20.78 8.88 20.42 8.39 20.05 7.93" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M15.51 12.7C15.25 14.11 14.1 15.26 12.69 15.52" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9.47 14.53L2 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M22 2L14.53 9.47" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15.58 12C15.58 13.98 13.98 15.58 12 15.58C10.02 15.58 8.42 13.98 8.42 12C8.42 10.02 10.02 8.42 12 8.42C13.98 8.42 15.58 10.02 15.58 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.4C18.82 5.8 15.53 3.73 12 3.73C8.47 3.73 5.18 5.81 2.89 9.41C1.99 10.82 1.99 13.19 2.89 14.6C5.18 18.19 8.47 20.27 12 20.27Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  disabled={loading}
                  style={{
                    backgroundColor: institution ? theme.primary_color : undefined,
                    borderColor: institution ? theme.primary_color : undefined,
                  }}
                  onMouseEnter={(e) => {
                    if (institution && theme.secondary_color) {
                      e.currentTarget.style.backgroundColor = theme.secondary_color;
                      e.currentTarget.style.borderColor = theme.secondary_color;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (institution && theme.primary_color) {
                      e.currentTarget.style.backgroundColor = theme.primary_color;
                      e.currentTarget.style.borderColor = theme.primary_color;
                    }
                  }}
                >
                  {/* Show login/signup button based on mode, except for institutions that don't allow signups */}
                  {(institution && !signupsAllowed) ? t('login.form.loginButton') : (isLogin ? t('login.form.loginButton') : t('login.form.signupButton'))}
                  {loading && <span className="auth-loading"></span>}
                </button>
              </form>
              
              {/* Show Google button if allowed */}
              {googleLoginAllowed && (
                <>
                  <div className="auth-divider">
                    <span>{t('login.form.or')}</span>
                  </div>
                  
                  <button 
                    onClick={handleGoogleSignIn} 
                    disabled={loading}
                    className="google-sign-in-button"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                      <path fill="none" d="M1 1h22v22H1z" />
                    </svg>
                    {t('login.form.googleButton')}
                    {loading && <span className="auth-loading"></span>}
                  </button>
                </>
              )}
              
              {/* Show signup toggle if signups are allowed */}
              {signupsAllowed && (
                <div className="auth-toggle">
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      switchMode();
                    }}
                    href="#"
                    className="toggle-link"
                  >
                    {isLogin ? t('login.toggle.signupPrompt') : t('login.toggle.loginPrompt')}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 